import { hasDevanagari, isPlainToken, transliterateSegment } from '../engine';
import { convertWord } from './memory';

/**
 * What happens as somebody types.
 *
 * The rules are here rather than in each field implementation because there are
 * two of those — the widget's plain DOM one and the app's React one — and they
 * have already drifted once. What a space does, what a backspace does, and what
 * counts as "still the same word" are one answer, used twice.
 */

/** The word most recently converted, and where it sits. */
export interface Conversion {
  roman: string;
  /** The Devanagari that was inserted, kept so the record can be invalidated. */
  text: string;
  from: number;
  to: number;
}

/** A replacement for the whole field value, and where the caret goes after it. */
export interface Edit {
  value: string;
  caret: number;
  conversion: Conversion | null;
}

/** Characters that can continue a romanized word. */
const ROMAN_TAIL = /^[A-Za-z0-9\\~^]+$/;

/**
 * Whether what was typed after a converted word continues it.
 *
 * Exported because the rich editor needs the same answer and used not to ask it
 * — typing `ko` onto नेपाल there produced नेपालko and stayed that way. One rule,
 * three implementations of the field around it.
 */
export function isRomanTail(text: string): boolean {
  return text.length > 0 && ROMAN_TAIL.test(text);
}

/**
 * Whether a record still describes the text.
 *
 * The field may have been edited anywhere — a paste, a selection replaced, a
 * caret moved back three words — and a stale range would make the picker offer
 * readings of text that is no longer there, or merge a new word onto the wrong
 * one. Cheap to check, and checking is what makes the rest safe to assume.
 */
export function stillValid(value: string, last: Conversion | null): last is Conversion {
  return last !== null && value.slice(last.from, last.to) === last.text;
}

/**
 * The romanized token the caret is sitting at the end of.
 *
 * Two shapes. Ordinarily it is the run of non-space characters behind the caret.
 * But when the caret is just past a word this converted a moment ago, and roman
 * letters have been typed straight onto it, the token is the *whole* thing —
 * the original spelling plus what was added.
 *
 * That second case is what makes `nepal` → नेपाल, backspace, `ko` → नेपालको
 * rather than नेपाल followed by an unconvertible नेपालko. It also means the
 * suffix rules see `nepalko` and produce the form Nepali actually uses.
 */
function tokenAt(value: string, caret: number, last: Conversion | null): { roman: string; from: number } | null {
  if (stillValid(value, last) && caret > last.to) {
    const tail = value.slice(last.to, caret);
    if (ROMAN_TAIL.test(tail)) return { roman: last.roman + tail, from: last.from };
  }

  const before = value.slice(0, caret);
  const match = /\S+$/.exec(before);
  if (!match) return null;

  return { roman: match[0], from: before.length - match[0].length };
}

/**
 * Space: convert the word, and add the space.
 *
 * The space is inserted here rather than left to the browser's default action,
 * and the caller stops that default. Letting it through works in a plain field
 * and loses the space in a React one — the conversion sets state, React
 * re-renders from that state, and the space the browser had already typed into
 * the DOM is overwritten by a value that never had it.
 *
 * Returns null when there is nothing to convert, and the caller lets the space
 * through untouched.
 */
export function commitOnSpace(value: string, caret: number, last: Conversion | null): Edit | null {
  const token = tokenAt(value, caret, last);
  if (!token) return null;

  const { roman, from } = token;
  // Already Devanagari means it was typed directly — unless this is a merge,
  // where the head is Devanagari by construction and `roman` is the spelling.
  const merging = from !== caret - roman.length;
  if (!merging && hasDevanagari(roman)) return null;

  const plain = isPlainToken(roman);
  const converted = plain ? convertWord(roman) : transliterateSegment(roman);
  if (converted === roman) return null;

  const head = value.slice(0, from);
  const tail = value.slice(caret);

  return {
    value: `${head}${converted} ${tail}`,
    caret: from + converted.length + 1,
    conversion: plain ? { roman, text: converted, from, to: from + converted.length } : null,
  };
}

/**
 * Backspace immediately after a conversion: take the space back and offer the
 * other readings.
 *
 * The moment somebody presses backspace there is the moment they have read what
 * appeared and disagreed with it. Opening the picker then is the difference
 * between a correction that takes one keystroke and one that takes deleting the
 * word and knowing how to spell around the engine.
 *
 * Returns null for every other backspace, which is then an ordinary delete.
 */
export function backspaceAfterConversion(
  value: string,
  caret: number,
  last: Conversion | null,
): Edit | null {
  if (!stillValid(value, last)) return null;
  if (caret !== last.to + 1 || value[last.to] !== ' ') return null;

  return {
    value: value.slice(0, last.to) + value.slice(caret),
    caret: last.to,
    conversion: { ...last },
  };
}

/**
 * Where the alternatives picker should point.
 *
 * The start of the word, not the caret: the list is about that word, and after a
 * merge or a space the caret is somewhere past it.
 */
export function anchorIndex(last: Conversion): number {
  return last.from;
}

/** The whitespace-delimited token surrounding an index. */
export function tokenAround(value: string, index: number): { text: string; from: number; to: number } | null {
  if (!value) return null;

  let from = index;
  let to = index;
  while (from > 0 && !/\s/.test(value[from - 1])) from--;
  while (to < value.length && !/\s/.test(value[to])) to++;

  return to > from ? { text: value.slice(from, to), from, to } : null;
}

/**
 * Every word this field has converted, so any of them can be reconsidered.
 *
 * Keyed by the Devanagari rather than by position. Positions go stale the moment
 * anything is typed, pasted or deleted earlier in the field, and a stale range
 * would offer readings of whatever had moved into it; the text is stable, and
 * where it currently sits can be worked out at the moment somebody clicks.
 *
 * That is what makes an *older* word clickable and not only the last one — and
 * clicking is the only way to reach the picker at all on a phone, where there is
 * no Ctrl+Space to press.
 */
export class ConversionLog {
  /** Devanagari → the spelling that produced it. Most recent wins a repeat. */
  private readonly romanByText = new Map<string, string>();

  /** Bounded: this is a typing session, not a document history. */
  constructor(private readonly limit = 200) {}

  remember(conversion: Conversion | null): void {
    if (!conversion) return;

    // Re-inserted so a word used again becomes the newest entry.
    this.romanByText.delete(conversion.text);
    this.romanByText.set(conversion.text, conversion.roman);

    if (this.romanByText.size > this.limit) {
      const oldest = this.romanByText.keys().next().value;
      if (oldest !== undefined) this.romanByText.delete(oldest);
    }
  }

  /**
   * The conversion behind the word at `index`, if this field produced it.
   *
   * Null for a word typed in Devanagari directly, or converted in some other
   * session — there is no way back from the script to the spelling, and
   * guessing one would offer readings of a word nobody asked about.
   */
  find(value: string, index: number): Conversion | null {
    const token = tokenAround(value, index);
    if (!token) return null;

    const roman = this.romanByText.get(token.text);
    return roman ? { roman, text: token.text, from: token.from, to: token.to } : null;
  }
}
